import * as React from 'react'

import { PartialCategory } from '@doist/help-center-utils'
import * as Marketist from '@doist/marketist'
import { Languages } from '@doist/marketist/dist/components/LanguagePicker/languages'

import HtmlToReact from 'html-react-parser'
import { parse } from 'marked'
import { TFunction, useTranslation } from 'next-i18next'
import * as BannerStatus from 'services/bannersStatus'
import { useBannerStatus } from 'services/hooks/useBannerStatus'
import { useLoginLink } from 'services/loginLink'
import { User } from 'types'
import { PageBanner } from 'types/contentful'

import { MobileSearch } from '../../MobileSearch/MobileSearch'
import { SearchBar } from '../SearchBar/SearchBar'

import { LanguageHeaderLink } from './LanguageHeaderLink/LanguageHeaderLink'
import LogoWordmark from './tw-help-logo.svg'
import LogoMobile from './tw-help-logo-small.svg'

import css from './Header.module.css'

function getLanguageHeaderItem(language: Languages) {
    return function () {
        return <LanguageHeaderLink currentLanguage={language} />
    }
}

function getMobileSearchHeaderItem(title: string, language: string) {
    return function () {
        return <MobileSearch title={title} language={language} />
    }
}

/** The HelpHeader react component. */
export type HelpHeaderProps = {
    user?: User
    categories: PartialCategory[]
    showContactUs?: boolean
    currentPath: string
    articleId?: number
    searchText?: string
    searchBarEnabled?: boolean
    pageBanner?: PageBanner
}
export function HelpHeader({
    user,
    categories,
    showContactUs = false,
    currentPath,
    articleId,
    searchText,
    searchBarEnabled = false,
    pageBanner,
}: HelpHeaderProps) {
    const { t, i18n } = useTranslation('helpcenter')
    const loginUrl = useLoginLink({ queryString: `success_page=${currentPath}` })
    const { onCloseBanner, status } = useBannerStatus(pageBanner)

    const languageLink = new Marketist.HeaderLink({
        customComponent: getLanguageHeaderItem(i18n.language as Languages),
    })

    const rightLinks = []
    if (user) {
        rightLinks.push(getProfileDropdown(t, currentPath, user, articleId, true))
    } else {
        rightLinks.push(
            new Marketist.HeaderLink({
                text: t('menu.login'),
                href: loginUrl,
            }),
        )
    }
    if (showContactUs) {
        rightLinks.push(
            new Marketist.HeaderLink({
                text: t('menu.contact'),
                href: '/help/articles/5863164524572',
                primary: true,
            }),
        )
    }
    rightLinks.push(languageLink)

    const mobileBottomLinks = [
        new Marketist.HeaderLink({
            text: t('menu.login'),
            href: loginUrl,
        }),
        new Marketist.HeaderLink({
            text: t('menu.contact'),
            href: '/help/articles/5863164524572',
            primary: true,
        }),
    ]

    const mobileLinks = categories.map(
        (category) =>
            new Marketist.HeaderDropdownLinks({
                title: category.name,
                links: category.sections.map(
                    (section) =>
                        new Marketist.HeaderLink({
                            text: section.name,
                            description: section.description,
                            href: section.href,
                        }),
                ),
            }),
    )
    if (user) {
        mobileLinks.push(getProfileDropdown(t, currentPath, user, articleId))
    }

    const mobileRightLinks = []
    if (searchBarEnabled) {
        const searchButton = new Marketist.HeaderLink({
            customComponent: getMobileSearchHeaderItem(t('search'), i18n.language),
        })
        mobileRightLinks.push(searchButton)
    }
    mobileRightLinks.push(languageLink)
    return (
        <Marketist.Header
            homeLink="/help"
            openMenuLabel={t('menu.openMenu')}
            closeMenuLabel={t('menu.closeMenu')}
            homeLabel={t('menu.home')}
            className={css.header}
            customLogoWordmark={LogoWordmark}
            customLogoMobile={LogoMobile}
            rightLinks={rightLinks}
            mobileLinks={mobileLinks}
            mobileRightLinks={mobileRightLinks}
            mobileBottomLinks={mobileBottomLinks}
            middleComponent={
                searchBarEnabled ? (
                    <SearchBar
                        title={t('search')}
                        language={i18n.language}
                        initialSearchText={searchText}
                        className={css.searchBar}
                        applyHeaderStyle
                    />
                ) : null
            }
            bannerContent={
                pageBanner && status === BannerStatus.Status.NotSeen
                    ? HtmlToReact(parse(pageBanner.message))
                    : null
            }
            onBannerClosed={onCloseBanner}
        />
    )
}

function getProfileDropdown(
    t: TFunction,
    currentPath: string,
    user: User,
    articleId?: number,
    withName = false,
) {
    const dropdownLinks = []

    if (articleId && user.isDoister) {
        dropdownLinks.push({
            text: t('menu.editArticle'),
            href: `/help/preview?id=${articleId}`,
        })
    }

    dropdownLinks.push({
        text: t('menu.myTickets'),
        href: '/help/contact/requests',
    })

    dropdownLinks.push({
        text: t('menu.logout'),
        href: `/logout?redirect_uri=${currentPath}`,
        primary: true,
    })

    return new Marketist.HeaderDropdownLinks({
        avatarUrl: user.avatar_urls.s60,
        title: withName && user.avatar_urls.s60 !== undefined ? '' : user.short_name,
        links: dropdownLinks,
        size: 'small',
        rightSided: true,
    })
}
